/*
 * bc-primitive-kit-builder
 *
 * The Primitive Kit Builder configurator (Phase 1 framework). Owns the central
 * state object, applies presets, drives step navigation, computes live totals
 * (price / weight / max-distance stub), serializes the build to the URL (so any
 * build is a shareable permalink), renders a printable build sheet, and adds the
 * configured components to the cart as individual line items via /cart/add.js.
 *
 * Config is read once from a <script type="application/json" data-bc-kit-config>
 * child (emitted by snippets/bc-kit-config.liquid). The diagram is a separate
 * element (<bc-kit-scene>); this element only calls scene.render(state) /
 * scene.zoomTo(step) — it never reaches into the scene's internals, so the P1
 * placeholder scene can be replaced by the P2 parametric scene with no rework.
 *
 * Cart contract mirrors assets/bc-custom-length.js (custom-length line-item
 * properties, ceil(length) — the kit is one piece) and assets/bc-accessories.js
 * (batch add + cart:update / cart:error dispatch).
 */
(() => {
  if (customElements.get('bc-primitive-kit-builder')) return;

  // Deliberately a touch above the real 3.28084 so the feet readout rounds up —
  // a slightly generous length estimate for customers thinking in feet.
  const FT_PER_M = 3.3;

  class BcPrimitiveKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en';
      this.lengthProperty = this.dataset.lengthProperty || 'Piece Length';
      this.piecesProperty = this.dataset.piecesProperty || 'Qty';

      this.config = this._readConfig();
      if (!this.config) return;

      this.slotByRole = {};
      (this.config.slots || []).forEach((s) => { this.slotByRole[s.role] = s; });
      this.options = this.config.options || {};
      this.presets = this.config.presets || [];

      this.money = window.bcMoney.formatter(this.locale, this.currency);

      this.scene = this.querySelector('bc-kit-scene');
      this.state = { slots: {} };
      this._maxLength = { label: 'Max rigable length', value: '' };

      this._initState();
      this._applyUrl();          // ?preset= + per-slot overrides
      this._syncControls();      // reflect state into DOM controls
      this._renderAllSubcontrols();
      this._wireEvents();
      this.recompute();          // totals + summary + max-length meta
      this._renderScene();
      this._observeStage();      // keep --bc-kit-stage-h in sync for the sticky summary
      this._observeScroll();     // scrollspy + compact-diagram-on-scroll
    }

    disconnectedCallback() {
      if (this._stageRO) { this._stageRO.disconnect(); this._stageRO = null; }
      if (this._stageResize) { window.removeEventListener('resize', this._stageResize); this._stageResize = null; }
      if (this._scrollFn) {
        window.removeEventListener('scroll', this._scrollFn);
        window.removeEventListener('resize', this._scrollFn);
        this._scrollFn = null;
      }
    }

    // ---- config ----------------------------------------------------------

    _readConfig() {
      try {
        const el = this.querySelector('script[data-bc-kit-config]');
        return JSON.parse(el?.textContent || 'null');
      } catch (e) {
        console.error('[bc-kit] bad config JSON', e);
        return null;
      }
    }

    _firstVariantValue(handle) {
      const opt = this.options[handle];
      const keys = opt && opt.variants ? Object.keys(opt.variants) : [];
      return keys.length ? keys[0] : '';
    }

    _slotOptionHandles(slot) {
      return slot.optionHandles || [];
    }

    // ---- state -----------------------------------------------------------

    _initState() {
      (this.config.slots || []).forEach((slot) => {
        const role = slot.role;
        if (slot.selectionType === 'numeric') {
          this.state.slots[role] = { numeric: Number(slot.numeric?.default ?? 0) };
        } else if (slot.selectionType === 'toggle') {
          this.state.slots[role] = { on: false };
        } else {
          const handle = slot.defaultOption || this._slotOptionHandles(slot)[0] || null;
          this.state.slots[role] = this._defaultSlotState(role, handle);
        }
      });
    }

    // Build the default sub-state for a single-select slot given a chosen option.
    _defaultSlotState(role, handle) {
      const opt = handle ? this.options[handle] : null;
      const st = {
        optionHandle: handle,
        variantValue: handle ? this._firstVariantValue(handle) : '',
        custom: false,
        length: 0,
        quantity: opt ? Number(opt.defaultQuantity || 1) : 1,
        colorOnTop: 'upper',
      };
      // Seed a sensible custom length so a custom-only product is orderable.
      if (opt && opt.custom && !Object.keys(opt.variants || {}).length) {
        st.custom = true;
        st.length = 1;
      }
      return st;
    }

    // ---- option / variant resolution ------------------------------------

    _resolveVariant(role) {
      const slot = this.slotByRole[role];
      const ss = this.state.slots[role];
      if (!slot || !ss || !ss.optionHandle) return null;
      const opt = this.options[ss.optionHandle];
      if (!opt || opt.kind !== 'product') return null;
      if (ss.custom && opt.custom) {
        return { variant: opt.custom, custom: true, opt };
      }
      const variants = opt.variants || {};
      let v = variants[ss.variantValue];
      if (!v && variants['']) v = variants[''];
      if (!v) {
        const keys = Object.keys(variants);
        if (keys.length) v = variants[keys[0]];
      }
      return v ? { variant: v, custom: false, opt } : null;
    }

    // The cart line + cost for one slot, or null if it contributes nothing.
    _line(role) {
      const slot = this.slotByRole[role];
      const ss = this.state.slots[role];
      if (!slot || !ss) return null;
      const resolved = this._resolveVariant(role);
      if (!resolved) return null;
      const { variant, custom, opt } = resolved;

      let quantity;
      let properties = null;
      if (custom) {
        const length = Number(ss.length) || 0;
        if (length <= 0) return null;
        // the kit is one line — always a single piece (quantity = ceil(length))
        quantity = Math.max(1, Math.ceil(length));
        const unit = opt.custom.unit || '';
        properties = {
          [this.lengthProperty]: unit ? `${length} ${unit}` : String(length),
          [this.piecesProperty]: '1',
        };
      } else if (opt.quantitySource === 'user') {
        quantity = Math.max(1, parseInt(ss.quantity, 10) || 1);
      } else {
        quantity = Math.max(1, parseInt(opt.defaultQuantity, 10) || 1);
      }

      // Sewn-loop ends ride as line-item properties on the webbing line —
      // forward-consistent with the planned bc-end-treatment property system
      // (the variant is already pinned to these end values server-side).
      if (role === 'webbing') {
        const ends = (this.config.settings && this.config.settings.webbingEndProperties) || null;
        if (ends) properties = { ...(properties || {}), ...ends };
        // Color-on-top is a fulfillment instruction on the webbing line.
        if (opt.scene && opt.scene.twoTone) {
          properties = properties || {};
          properties['Top Color'] = ss.colorOnTop === 'lower' ? 'Reversed' : 'Default';
        }
      }

      return {
        role,
        variantId: variant.id,
        quantity,
        priceCents: (variant.price || 0) * quantity,
        weightGrams: (variant.weight || 0) * quantity,
        properties,
        label: opt.label,
        custom,
        unit: custom ? opt.custom.unit : '',
        length: custom ? Number(ss.length) || 0 : null,
        variantValue: ss.variantValue,
      };
    }

    _allLines() {
      return (this.config.slots || [])
        .map((s) => this._line(s.role))
        .filter(Boolean);
    }

    // ---- totals ----------------------------------------------------------

    recompute() {
      const lines = this._allLines();
      const cents = lines.reduce((a, l) => a + l.priceCents, 0);
      const grams = lines.reduce((a, l) => a + l.weightGrams, 0);

      this._setText('[data-bc-total-price]', this._money(cents));
      this._setText('[data-bc-total-weight]', this._weight(grams));

      // Max rigable length is drawn as a dimension on the scene, not a totals
      // card. computeMaxDistance() is still a stub (needs the SME formula), so
      // the dimension shows its label alone until a value is available.
      const dist = this.computeMaxDistance(this.state);
      this._maxLength = {
        label: this.config.settings?.distanceLabel || 'Max rigable length',
        value: dist == null ? '' : `${dist} m`,
      };

      this._renderSummary();
      this.dispatchEvent(new CustomEvent('bc:kit:state', { bubbles: true, detail: { state: this.state } }));
    }

    // Stubbed in P1 — the real formula needs SME input (strength/stretch/anchor).
    computeMaxDistance() {
      return null;
    }

    _money(cents) {
      return this.money.format((cents || 0) / 100);
    }

    _weight(grams) {
      const g = grams || 0;
      // bcWeightFormat's contract is (value-already-in-unit, unit) — convert
      // the gram total first via fromGrams, or a 3,807 g kit would print as
      // "3,807.0 kg". Same formatter the PDP weight rows use, so they agree.
      const wf = window.bcWeightFormat;
      if (wf && typeof wf.formatWeight === 'function' && typeof wf.fromGrams === 'function') {
        try {
          return `${wf.formatWeight(wf.fromGrams(g, 'kg'), 'kg')} (${wf.formatWeight(wf.fromGrams(g, 'lb'), 'lb')})`;
        } catch (_e) { /* fall through */ }
      }
      const kg = g / 1000;
      const lb = g * 0.00220462;
      const primary = kg >= 1 ? `${kg.toFixed(2)} kg` : `${Math.round(g)} g`;
      return `${primary} (${lb.toFixed(2)} lb)`;
    }

    // ---- build summary (left column) -------------------------------------

    // Rebuild the left summary, grouped by step to mirror the build flow: a
    // clickable step header (jumps to the step) over its component rows (each
    // jumps to its own control). Single-select rows carry their line price.
    _renderSummary() {
      const host = this.querySelector('[data-bc-summary]');
      if (!host) return;
      const byStep = new Map();
      (this.config.slots || []).forEach((slot) => {
        const step = Number(slot.step) || 0;
        if (!byStep.has(step)) byStep.set(step, []);
        byStep.get(step).push(slot);
      });
      const steps = Array.from(byStep.keys()).sort((a, b) => a - b);
      host.innerHTML = steps.map((step) => {
        const rows = byStep.get(step).map((slot) => this._summaryRow(slot)).join('');
        return (
          `<div class="bc-kit__sum-group" data-bc-step-group="${step}">` +
          `<button type="button" class="bc-kit__sum-step" data-bc-focus-step="${step}">` +
          `<span class="bc-kit__sum-step-num">Step ${step}</span>` +
          `<span class="bc-kit__sum-step-title">${this._esc(this._stepTitle(step))}</span>` +
          `</button>` +
          `<div class="bc-kit__sum-rows">${rows}</div>` +
          `</div>`
        );
      }).join('');
      // re-apply the scrollspy highlight (the rebuild wiped the is-active class)
      if (this._activeStep != null) this._highlightSummary(this._activeStep);
    }

    // One component row for the summary.
    _summaryRow(slot) {
      const role = slot.role;
      const val = this._summaryText(role);
      const line = slot.selectionType === 'single' ? this._line(role) : null;
      const price = line ? this._money(line.priceCents) : '';
      const empty = (val === '—' || val === 'Not included') ? ' data-empty="1"' : '';
      return (
        `<button type="button" class="bc-kit__sum-row" data-bc-focus="${this._attr(role)}"${empty}>` +
        `<span class="bc-kit__sum-name">${this._esc(slot.label || role)}</span>` +
        `<span class="bc-kit__sum-val">${this._esc(val)}</span>` +
        (price ? `<span class="bc-kit__sum-price">${this._esc(price)}</span>` : '') +
        `</button>`
      );
    }

    // Step title from the rendered step section (single source — the Liquid
    // step_titles array); cached on first read.
    _stepTitle(n) {
      if (!this._stepTitles) {
        this._stepTitles = {};
        this.querySelectorAll('.bc-kit__step').forEach((el) => {
          const s = Number(el.getAttribute('data-bc-step'));
          const t = el.querySelector('.bc-kit__step-title');
          if (s && t) this._stepTitles[s] = t.textContent.trim();
        });
      }
      return this._stepTitles[n] || `Step ${n}`;
    }

    // Human-readable current selection for one slot.
    _summaryText(role) {
      const slot = this.slotByRole[role];
      const ss = this.state.slots[role];
      if (!slot || !ss) return '';
      if (slot.selectionType === 'numeric') {
        const unit = (slot.numeric && slot.numeric.unit) || '';
        return `${ss.numeric}${unit ? ` ${unit}` : ''}`;
      }
      if (slot.selectionType === 'toggle') {
        return ss.on ? 'Included' : 'Not included';
      }
      const opt = ss.optionHandle ? this.options[ss.optionHandle] : null;
      if (!opt) return '—';
      const label = opt.label || ss.optionHandle;
      const bits = [];
      if (ss.custom && opt.custom) {
        const unit = opt.custom.unit || '';
        bits.push(`${ss.length || 0}${unit ? ` ${unit}` : ''}`);
      } else if (ss.variantValue) {
        bits.push(ss.variantValue);
      }
      if (opt.quantitySource === 'user' && ss.quantity > 1) bits.push(`× ${ss.quantity}`);
      return bits.length ? `${label} — ${bits.join(' ')}` : label;
    }

    // ---- subcontrols (length / qty / color-on-top) -----------------------

    _renderAllSubcontrols() {
      (this.config.slots || []).forEach((slot) => {
        if (slot.selectionType === 'single') this._renderSubcontrol(slot.role);
      });
    }

    _renderSubcontrol(role) {
      const fs = this.querySelector(`.bc-kit__slot[data-bc-slot="${role}"]`);
      const host = fs ? fs.querySelector('[data-bc-subcontrol]') : null;
      if (!host) return;
      const ss = this.state.slots[role];
      const opt = ss && ss.optionHandle ? this.options[ss.optionHandle] : null;
      if (!opt || opt.kind !== 'product') {
        host.innerHTML = '';
        host.hidden = true;
        return;
      }

      const parts = [];
      const variants = opt.variants || {};
      const variantKeys = Object.keys(variants).filter((k) => k !== '');
      const lengthMode = opt.lengthSource === 'variant' || opt.lengthSource === 'variant_or_custom';

      // (a) non-length variant pick (e.g. MightyLock Color)
      if (opt.selectOption && opt.lengthSource === 'none' && variantKeys.length) {
        parts.push(this._selectHtml('bc-length-variant', opt.selectOption, variantKeys, ss.variantValue));
      }

      // (b) length: predetermined values + optional custom
      if (lengthMode && (variantKeys.length || opt.custom)) {
        const items = variantKeys.slice();
        if (opt.custom) items.push('__custom__');
        const current = ss.custom ? '__custom__' : ss.variantValue;
        parts.push(this._selectHtml('bc-length', 'Length', items, current, { customLabel: true }));
        if (ss.custom) {
          // Custom length shows only the piece length — the kit is one line, so
          // the quantity is always 1 (no pieces box).
          parts.push(this._customLengthField(opt.custom.unit, ss.length || 1));
        }
      }

      // (c) custom-only length (sold-by-length)
      if (opt.lengthSource === 'custom' && opt.custom) {
        parts.push(this._customLengthField(opt.custom.unit, ss.length || 1));
      }

      // (d) user quantity (e.g. tree-protection pieces)
      if (opt.quantitySource === 'user') {
        parts.push(
          `<div class="bc-kit__sub-field"><span>Quantity</span>` +
          `<div class="bc-kit__qty" data-bc-quantity>` +
          `<button type="button" class="bc-kit__qty-btn" data-bc-qty-minus aria-label="Decrease">−</button>` +
          `<input type="number" class="bc-kit__qty-input" data-bc-qty min="1" step="1" value="${this._attr(ss.quantity || 1)}">` +
          `<button type="button" class="bc-kit__qty-btn" data-bc-qty-plus aria-label="Increase">+</button>` +
          `</div></div>`
        );
      }

      // (e) which color is on top (two-tone webbings only)
      if (role === 'webbing' && opt.scene && opt.scene.twoTone) {
        const up = opt.scene.color || '#888';
        const lo = opt.scene.colorLower || up;
        parts.push(
          `<div class="bc-kit__sub-field"><span>Top color</span>` +
          `<div class="bc-kit__toptone" role="radiogroup" aria-label="Which color is on top">` +
          `<button type="button" class="bc-kit__swatch${ss.colorOnTop !== 'lower' ? ' is-active' : ''}" data-bc-color-top="upper" style="background:${this._attr(up)}" aria-label="Default"></button>` +
          `<button type="button" class="bc-kit__swatch${ss.colorOnTop === 'lower' ? ' is-active' : ''}" data-bc-color-top="lower" style="background:${this._attr(lo)}" aria-label="Reversed"></button>` +
          `</div></div>`
        );
      }

      // (f) tiny webbing color preview
      if (role === 'webbing' && opt.scene) {
        const top = ss.colorOnTop === 'lower' ? (opt.scene.colorLower || opt.scene.color) : opt.scene.color;
        if (top) {
          parts.push(
            `<svg class="bc-kit__web-preview" viewBox="0 0 200 24" aria-hidden="true">` +
            `<line x1="6" y1="12" x2="194" y2="12" stroke="${this._attr(top)}" stroke-width="10" stroke-linecap="round"/></svg>`
          );
        }
      }

      host.innerHTML = parts.join('');
      host.hidden = parts.length === 0;
      this._placeSubcontrol(fs, host);
    }

    // In the vertical radio lists, float the sub-control (length/qty) in right
    // under the chosen option so it "pops up" next to the selection. Select-based
    // slots (the webbing dropdown) have no .bc-kit__options — leave the host put.
    _placeSubcontrol(fs, host) {
      const list = fs && fs.querySelector('.bc-kit__options');
      if (!list) return;
      const checked = list.querySelector('input[type="radio"]:checked');
      const label = checked && checked.closest('.bc-kit__option');
      if (label && label.nextElementSibling !== host) label.after(host);
    }

    // A compact custom-length field: a small number input with the unit shown
    // inside the box (e.g. "20 m") + a live converted value beside it (m↔ft).
    _customLengthField(unit, value) {
      const u = unit || '';
      return (
        `<div class="bc-kit__sub-field"><span>Length</span>` +
        `<div class="bc-kit__len">` +
        `<span class="bc-kit__len-box">` +
        `<input type="number" inputmode="decimal" min="0.1" step="0.1" data-bc-custom-length ` +
        `aria-label="${this._attr(u ? `Length in ${u}` : 'Length')}" value="${this._attr(value)}">` +
        (u ? `<span class="bc-kit__len-unit">${this._esc(u)}</span>` : '') +
        `</span>` +
        `<span class="bc-kit__len-conv" data-bc-length-conv>${this._esc(this._lengthConv(u, value))}</span>` +
        `</div></div>`
      );
    }

    // Convert a length to the other common unit for a side-by-side readout.
    _lengthConv(unit, value) {
      const v = Number(value) || 0;
      if (!v) return '';
      const u = String(unit || '').toLowerCase();
      if (u === 'm') return `≈ ${(v * FT_PER_M).toFixed(1)} ft`;
      if (u === 'ft') return `≈ ${(v / FT_PER_M).toFixed(2)} m`;
      return '';
    }

    _selectHtml(dataKey, label, values, current, opts = {}) {
      const optionsHtml = values.map((v) => {
        const lbl = v === '__custom__' ? 'Custom length' : v;
        const sel = String(v) === String(current) ? ' selected' : '';
        return `<option value="${this._attr(v)}"${sel}>${this._esc(lbl)}</option>`;
      }).join('');
      return (
        `<label class="bc-kit__sub-field"><span>${this._esc(label)}</span>` +
        `<select data-${dataKey}>${optionsHtml}</select></label>`
      );
    }

    // ---- DOM sync --------------------------------------------------------

    _syncControls() {
      (this.config.slots || []).forEach((slot) => {
        const role = slot.role;
        const ss = this.state.slots[role];
        const fs = this.querySelector(`.bc-kit__slot[data-bc-slot="${role}"]`);
        if (!fs || !ss) return;
        if (slot.selectionType === 'numeric') {
          fs.querySelectorAll('[data-bc-numeric],[data-bc-numeric-range]').forEach((el) => { el.value = ss.numeric; });
          this._updateNumericReadout(role);
        } else if (slot.selectionType === 'toggle') {
          const cb = fs.querySelector('[data-bc-toggle]');
          if (cb) cb.checked = !!ss.on;
        } else {
          fs.querySelectorAll('[data-bc-option]').forEach((el) => {
            if (el.tagName === 'SELECT') el.value = ss.optionHandle || '';
            else if (el.type === 'radio') el.checked = el.value === ss.optionHandle;
          });
        }
      });
    }

    // Live readout under a numeric (tree-diameter) slider: the inches
    // equivalent of the cm value, plus the minimum sling length needed to wrap
    // a tree that size (its circumference, π·diameter — the same threshold the
    // scene uses to flag a sling as too short).
    _updateNumericReadout(role) {
      const slot = this.slotByRole[role];
      if (!slot || slot.selectionType !== 'numeric') return;
      const fs = this.querySelector(`.bc-kit__slot[data-bc-slot="${role}"]`);
      if (!fs) return;
      const unit = (slot.numeric && slot.numeric.unit) || '';
      const val = Number(this.state.slots[role] && this.state.slots[role].numeric) || 0;
      const isDiameter = role.indexOf('diameter') !== -1;

      // secondary unit shown inline next to the cm value
      const conv = fs.querySelector('[data-bc-numeric-conv]');
      if (conv) conv.textContent = (unit === 'cm' && val > 0) ? `≈ ${(val / 2.54).toFixed(1)} in` : '';

      // readout below: unsafe-tree warning + minimum sling length
      const out = fs.querySelector('[data-bc-numeric-readout]');
      if (!out) return;
      const parts = [];
      if (isDiameter && unit === 'cm' && val > 0) {
        if (val < 30) {
          parts.push(
            `<span class="bc-kit__readout-warn">⚠ Tree too small — a trunk under 30 cm is unsafe to rig on.</span>`
          );
        }
        // minimum sling length = trunk circumference (π·diameter)
        const circM = Math.PI * (val / 100);
        parts.push(
          `<span class="bc-kit__readout-sling">Min. sling length: ` +
          `<strong>${circM.toFixed(2)} m</strong> (${(circM * FT_PER_M).toFixed(1)} ft)</span>`
        );
      }
      out.innerHTML = parts.join('');
    }

    // ---- events ----------------------------------------------------------

    _wireEvents() {
      this.addEventListener('change', (e) => this._onChange(e));
      this.addEventListener('input', (e) => this._onInput(e));
      this.addEventListener('click', (e) => this._onClick(e));
      if (this.scene) {
        // Click a component in the scene to jump to its control. The scene tags
        // groups with data-bc-edit="<role>".
        this.scene.addEventListener('click', (e) => {
          const g = e.target.closest('[data-bc-edit]');
          if (!g) return;
          this._focusSlot(g.getAttribute('data-bc-edit'));
        });
      }
    }

    _slotRoleFromEvent(e) {
      const fs = e.target.closest('.bc-kit__slot');
      return fs ? fs.getAttribute('data-bc-slot') : null;
    }

    _onChange(e) {
      const t = e.target;
      const role = this._slotRoleFromEvent(e);
      if (t.matches('[data-bc-option]')) {
        const handle = t.value;
        this.state.slots[role] = this._defaultSlotState(role, handle);
        this._renderSubcontrol(role);
        this._afterChange();
      } else if (t.matches('[data-bc-toggle]')) {
        this.state.slots[role].on = t.checked;
        this._afterChange();
      } else if (t.matches('[data-bc-length]')) {
        const ss = this.state.slots[role];
        if (t.value === '__custom__') { ss.custom = true; if (!(ss.length > 0)) ss.length = 1; }
        else { ss.custom = false; ss.variantValue = t.value; }
        this._renderSubcontrol(role);
        this._afterChange();
      } else if (t.matches('[data-bc-length-variant]')) {
        this.state.slots[role].variantValue = t.value;
        this._afterChange();
      }
    }

    _onInput(e) {
      const t = e.target;
      const role = this._slotRoleFromEvent(e);
      if (t.matches('[data-bc-numeric]') || t.matches('[data-bc-numeric-range]')) {
        const val = Number(t.value);
        this.state.slots[role].numeric = val;
        // keep the paired range/number input in sync
        const fs = t.closest('.bc-kit__slot');
        fs.querySelectorAll('[data-bc-numeric],[data-bc-numeric-range]').forEach((el) => { if (el !== t) el.value = val; });
        this._updateNumericReadout(role);
        this._afterChange();
      } else if (t.matches('[data-bc-custom-length]')) {
        this.state.slots[role].length = Number(t.value) || 0;
        // update the m↔ft readout in place (re-rendering would drop focus)
        const conv = t.closest('.bc-kit__sub-field')?.querySelector('[data-bc-length-conv]');
        if (conv) {
          const opt = this.options[this.state.slots[role].optionHandle];
          conv.textContent = this._lengthConv(opt && opt.custom ? opt.custom.unit : '', this.state.slots[role].length);
        }
        this._afterChange();
      } else if (t.matches('[data-bc-qty]')) {
        this.state.slots[role].quantity = Math.max(1, parseInt(t.value, 10) || 1);
        this._afterChange();
      }
    }

    _onClick(e) {
      const t = e.target;
      if (t.closest('[data-bc-preset]')) {
        this.applyPreset(t.closest('[data-bc-preset]').getAttribute('data-bc-preset'));
      } else if (t.closest('[data-bc-focus-step]')) {
        this._focusStep(Number(t.closest('[data-bc-focus-step]').getAttribute('data-bc-focus-step')));
      } else if (t.closest('[data-bc-focus]')) {
        this._focusSlot(t.closest('[data-bc-focus]').getAttribute('data-bc-focus'));
      } else if (t.closest('[data-bc-qty-plus]') || t.closest('[data-bc-qty-minus]')) {
        const role = this._slotRoleFromEvent(e);
        const delta = t.closest('[data-bc-qty-plus]') ? 1 : -1;
        const ss = this.state.slots[role];
        ss.quantity = Math.max(1, (parseInt(ss.quantity, 10) || 1) + delta);
        const input = t.closest('.bc-kit__qty')?.querySelector('[data-bc-qty]');
        if (input) input.value = ss.quantity;
        this._afterChange();
      } else if (t.closest('[data-bc-color-top]')) {
        const role = this._slotRoleFromEvent(e);
        this.state.slots[role].colorOnTop = t.closest('[data-bc-color-top]').getAttribute('data-bc-color-top');
        this._renderSubcontrol(role);
        this._afterChange();
      } else if (t.closest('[data-bc-add]')) {
        this._addToCart();
      } else if (t.closest('[data-bc-copy-link]')) {
        this._copyLink();
      } else if (t.closest('[data-bc-print]')) {
        this._printBuildSheet();
      }
    }

    _afterChange() {
      this.recompute();
      this._renderScene();
      this._writeUrl();
    }

    // ---- navigation (single page) ----------------------------------------

    // Scroll a component's control into view (from a summary row or a scene
    // click) and flash it. The scene zoom follows the scroll (see _zoomScene).
    _focusSlot(role) {
      const fs = this.querySelector(`.bc-kit__slot[data-bc-slot="${role}"]`);
      if (fs) {
        fs.scrollIntoView({ behavior: this._reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        fs.classList.add('bc-kit__slot--flash');
        window.clearTimeout(this._flashT);
        this._flashT = window.setTimeout(() => fs.classList.remove('bc-kit__slot--flash'), 1100);
      }
    }

    // Scroll a whole step section into view (from a summary step header). The
    // scene zoom follows the scroll (see _zoomScene).
    _focusStep(n) {
      const sec = this.querySelector(`.bc-kit__step[data-bc-step="${n}"]`);
      if (sec) sec.scrollIntoView({ behavior: this._reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    _reduceMotion() {
      return typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // The diagram is sticky full-width on top; the summary sticks just beneath
    // it. Publish the live stage height so CSS can offset the summary's top.
    _observeStage() {
      const stage = this.querySelector('.bc-kit__stage');
      if (!stage) return;
      const setH = () => {
        this.style.setProperty('--bc-kit-stage-h', `${Math.round(stage.getBoundingClientRect().height)}px`);
      };
      setH();
      if (typeof ResizeObserver === 'function') {
        this._stageRO = new ResizeObserver(setH);
        this._stageRO.observe(stage);
      } else if (typeof window !== 'undefined') {
        this._stageResize = setH;
        window.addEventListener('resize', setH);
      }
    }

    // Track scroll to (a) compact the pinned diagram once you scroll into the
    // builder — reclaiming vertical space — and (b) drive a scrollspy that
    // advances the summary's active step as each section comes into view.
    _observeScroll() {
      if (typeof window === 'undefined') return;
      let ticking = false;
      this._scrollFn = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this._onScroll(); });
      };
      window.addEventListener('scroll', this._scrollFn, { passive: true });
      window.addEventListener('resize', this._scrollFn, { passive: true });
      this._onScroll();
    }

    _onScroll() {
      // (a) compact the diagram after the builder has scrolled up past the top
      this.classList.toggle('bc-kit--compact', this.getBoundingClientRect().top < -40);

      // (b) scrollspy: the active step is the last one whose top has passed just
      // below the pinned diagram's bottom edge
      const stage = this.querySelector('.bc-kit__stage');
      const ref = (stage ? stage.getBoundingClientRect().bottom : 0) + 24;
      let active = null;
      this.querySelectorAll('.bc-kit__step').forEach((s) => {
        if (s.getBoundingClientRect().top <= ref) active = Number(s.getAttribute('data-bc-step'));
      });
      if (active == null) {
        const first = this.querySelector('.bc-kit__step');
        if (first) active = Number(first.getAttribute('data-bc-step'));
      }
      if (active !== this._activeStep) {
        this._activeStep = active;
        this._highlightSummary(active);
        this._zoomScene(active);
      }
    }

    // Zoom the diagram to the region of the step currently in view (full scene
    // for the webbing/top step). Driven by scroll, so it tracks as you move
    // through the builder and resets to the full diagram at the top.
    _zoomScene(step) {
      if (!this.scene || typeof this.scene.zoomTo !== 'function') return;
      const slot = (this.config.slots || []).find((s) => s.step === step && s.sceneZoom && s.sceneZoom !== 'none');
      this.scene.zoomTo(slot ? slot.sceneZoom : 'none');
    }

    // Mark the active step's summary group; keep it visible if the summary has
    // its own scrollbar (without ever scrolling the page).
    _highlightSummary(step) {
      const summary = this.querySelector('.bc-kit__summary');
      this.querySelectorAll('.bc-kit__sum-group').forEach((g) => {
        const on = Number(g.getAttribute('data-bc-step-group')) === step;
        g.classList.toggle('is-active', on);
        if (on && summary && summary.scrollHeight > summary.clientHeight + 4) {
          const gr = g.getBoundingClientRect();
          const sr = summary.getBoundingClientRect();
          if (gr.top < sr.top + 4) summary.scrollTop -= (sr.top - gr.top) + 8;
          else if (gr.bottom > sr.bottom - 4) summary.scrollTop += (gr.bottom - sr.bottom) + 8;
        }
      });
    }

    // ---- presets ---------------------------------------------------------

    applyPreset(key, { silent = false } = {}) {
      const preset = this.presets.find((p) => p.key === key);
      if (!preset || !preset.selections) return;
      Object.keys(preset.selections).forEach((role) => {
        const slot = this.slotByRole[role];
        if (!slot) return;
        const sel = preset.selections[role] || {};
        if (slot.selectionType === 'numeric') {
          this.state.slots[role] = { numeric: Number(sel.numeric ?? slot.numeric?.default ?? 0) };
        } else if (slot.selectionType === 'toggle') {
          this.state.slots[role] = { on: !!sel.toggle };
        } else {
          const handle = sel.optionHandle || slot.defaultOption || this._slotOptionHandles(slot)[0] || null;
          const ss = this._defaultSlotState(role, handle);
          if (sel.variantOptionValue != null) ss.variantValue = String(sel.variantOptionValue);
          if (sel.length != null && this.options[handle] && this.options[handle].custom) {
            ss.custom = true; ss.length = Number(sel.length);
          }
          if (sel.quantity != null) ss.quantity = Math.max(1, parseInt(sel.quantity, 10) || 1);
          this.state.slots[role] = ss;
        }
      });
      this._syncControls();
      this._renderAllSubcontrols();
      this.recompute();
      this._renderScene();
      if (!silent) this._writeUrl();
    }

    // ---- URL state -------------------------------------------------------

    _applyUrl() {
      const params = new URLSearchParams(location.search);
      const presetKey = params.get('preset');
      if (presetKey) this.applyPreset(presetKey, { silent: true });

      (this.config.slots || []).forEach((slot) => {
        const role = slot.role;
        if (!params.has(role)) return;
        const ss = this.state.slots[role];
        if (slot.selectionType === 'numeric') {
          // Hand-edited/truncated share URLs can carry garbage — a NaN here
          // would poison the summary ("NaN cm"), suppress the <30 cm warning
          // and re-serialize into the next share URL. Keep the default then.
          const numeric = Number(params.get(role));
          if (Number.isFinite(numeric)) ss.numeric = numeric;
        } else if (slot.selectionType === 'toggle') {
          ss.on = params.get(role) === '1';
        } else {
          const handle = params.get(role);
          if (this.options[handle]) {
            const next = this._defaultSlotState(role, handle);
            if (params.has(`${role}_v`)) next.variantValue = params.get(`${role}_v`);
            const urlLength = Number(params.get(`${role}_len`));
            if (urlLength > 0 && this.options[handle] && this.options[handle].custom) {
              next.custom = true; next.length = urlLength;
            }
            if (params.has(`${role}_q`)) next.quantity = Math.max(1, parseInt(params.get(`${role}_q`), 10) || 1);
            if (params.has(`${role}_top`)) next.colorOnTop = params.get(`${role}_top`);
            this.state.slots[role] = next;
          }
        }
      });
    }

    _buildParams() {
      const params = new URLSearchParams();
      (this.config.slots || []).forEach((slot) => {
        const role = slot.role;
        const ss = this.state.slots[role];
        if (!ss) return;
        if (slot.selectionType === 'numeric') {
          params.set(role, String(ss.numeric));
        } else if (slot.selectionType === 'toggle') {
          if (ss.on) params.set(role, '1');
        } else if (ss.optionHandle) {
          params.set(role, ss.optionHandle);
          if (ss.custom) {
            params.set(`${role}_len`, String(ss.length));
          } else if (ss.variantValue) {
            params.set(`${role}_v`, ss.variantValue);
          }
          const opt = this.options[ss.optionHandle];
          if (opt && opt.quantitySource === 'user' && ss.quantity > 1) params.set(`${role}_q`, String(ss.quantity));
          if (role === 'webbing' && ss.colorOnTop === 'lower') params.set(`${role}_top`, 'lower');
        }
      });
      return params;
    }

    _currentUrl() {
      const params = this._buildParams();
      return `${location.origin}${location.pathname}?${params.toString()}`;
    }

    _writeUrl() {
      try {
        history.replaceState(null, '', `${location.pathname}?${this._buildParams().toString()}`);
      } catch (_e) { /* replaceState can throw in sandboxed contexts; ignore */ }
    }

    _copyLink() {
      const url = this._currentUrl();
      const done = () => this._flash('[data-bc-copy-link]', 'Link copied!');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(() => this._flash('[data-bc-copy-link]', url));
      } else {
        this._flash('[data-bc-copy-link]', url);
      }
    }

    // ---- scene -----------------------------------------------------------

    _renderScene() {
      if (this.scene && typeof this.scene.render === 'function') {
        try { this.scene.render(this.state, this.options, this._maxLength); } catch (e) { console.error('[bc-kit] scene render', e); }
      }
    }

    // ---- cart ------------------------------------------------------------

    async _addToCart() {
      if (this._adding) return;
      // A custom-length selection whose length is empty/0 contributes no
      // line at all (_line returns null) — for a slackline kit that means
      // silently adding every component EXCEPT the webbing. Block the add
      // and point at the offending slot instead.
      const missingLength = (this.config.slots || []).find((slot) => {
        const resolved = this._resolveVariant(slot.role);
        if (!resolved || !resolved.custom) return false;
        const ss = this.state.slots[slot.role];
        return !(Number(ss && ss.length) > 0);
      });
      if (missingLength) {
        this._showError(`Enter a length for "${missingLength.label || missingLength.role}" before adding.`);
        return;
      }
      const lines = this._allLines();
      const items = lines.map((l) => {
        const item = { id: Number(l.variantId), quantity: l.quantity };
        if (l.properties) item.properties = { ...l.properties };
        return item;
      });
      // Record the build permalink on the first line so the order links back.
      if (items.length) {
        items[0].properties = { ...(items[0].properties || {}), Build: this._currentUrl() };
      }
      if (!items.length) {
        this._showError('Pick at least one component to add.');
        return;
      }

      this._adding = true;
      this._setBusy(true);
      try {
        const { ok, response, cart } = await window.bcCartAdd.add(items);
        if (!ok) {
          this._showError(response.description || response.message || 'Could not add this kit to the cart.');
          this.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { source: 'bc-primitive-kit-builder', data: { message: response.message, description: response.description, errors: response.errors } },
          }));
          return;
        }

        this._showError('');
        window.bcCartAdd.dispatchCartUpdate(this, {
          source: 'bc-primitive-kit-builder',
          cart,
          response,
          itemCount: items.length,
        });
        this._flash('[data-bc-add]', 'Added!');
      } catch (err) {
        console.error('[bc-kit] cart/add failed', err);
        this._showError('Something went wrong adding to the cart. Please try again.');
      } finally {
        this._adding = false;
        this._setBusy(false);
      }
    }

    // ---- build sheet -----------------------------------------------------

    _printBuildSheet() {
      const host = this.querySelector('[data-bc-buildsheet]');
      if (!host) return;
      const lines = this._allLines();
      const cents = lines.reduce((a, l) => a + l.priceCents, 0);
      const grams = lines.reduce((a, l) => a + l.weightGrams, 0);

      const rows = lines.map((l) => {
        const detail = l.custom ? `${l.length} ${l.unit}` : (l.variantValue || '');
        return `<tr><td>${this._esc(l.label)}</td><td>${this._esc(detail)}</td><td>${l.quantity}</td><td>${this._esc(this._money(l.priceCents))}</td></tr>`;
      }).join('');

      const sceneSnap = (this.scene && typeof this.scene.snapshotSVG === 'function') ? this.scene.snapshotSVG() : '';

      host.innerHTML =
        `<div class="bc-kit-buildsheet__inner">` +
        `<h2 class="bc-kit-buildsheet__title">Your Primitive Kit Build</h2>` +
        (sceneSnap ? `<div class="bc-kit-buildsheet__scene">${sceneSnap}</div>` : '') +
        `<table class="bc-kit-buildsheet__table"><thead><tr><th>Component</th><th>Spec</th><th>Qty</th><th>Price</th></tr></thead>` +
        `<tbody>${rows}</tbody>` +
        `<tfoot><tr><th colspan="3">Total</th><th>${this._esc(this._money(cents))}</th></tr>` +
        `<tr><td colspan="3">Total weight</td><td>${this._esc(this._weight(grams))}</td></tr></tfoot></table>` +
        `<p class="bc-kit-buildsheet__link">Re-open this build: ${this._esc(this._currentUrl())}</p>` +
        `</div>`;
      // The print CSS hides the whole page in favor of the build sheet ONLY
      // while this class is on <body> — a plain Cmd/Ctrl+P prints normally.
      // Cleanup rides afterprint (not the print() return) because iOS Safari
      // returns from print() before the user confirms the sheet.
      document.body.classList.add('bc-kit-printing');
      window.addEventListener('afterprint', () => document.body.classList.remove('bc-kit-printing'), { once: true });
      window.print();
    }

    // ---- helpers ---------------------------------------------------------

    _setText(sel, text) { const el = this.querySelector(sel); if (el) el.textContent = text; }
    _attr(v) { return String(v).replace(/"/g, '&quot;'); }
    _esc(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    _showError(msg) {
      const el = this.querySelector('[data-bc-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }
    _setBusy(on) {
      const btn = this.querySelector('[data-bc-add]');
      if (btn) { btn.disabled = on; btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }
    _flash(sel, msg) {
      const el = this.querySelector(sel);
      if (!el) return;
      // Cache the ORIGINAL label and clear any pending restore — two flashes
      // within 1.8s would otherwise capture the flash text as "original" and
      // leave the button permanently reading "Added!".
      if (!this._flashState) this._flashState = new Map();
      const state = this._flashState.get(el) || { original: el.textContent };
      if (state.timer) clearTimeout(state.timer);
      el.textContent = msg;
      state.timer = setTimeout(() => {
        el.textContent = state.original;
        this._flashState.delete(el);
      }, 1800);
      this._flashState.set(el, state);
    }
  }

  customElements.define('bc-primitive-kit-builder', BcPrimitiveKitBuilder);
})();
